/**
 * Installing the app where the browser offers it (part 40a, table P): Chromium sends `beforeinstallprompt` when the app
 * can be installed; its default (the browser's own banner) is stopped and the event kept, so the settings can offer
 * "Install" when the writer chooses. Browsers without the event (Firefox, Safari) offer nothing (P4); on iOS the Home
 * Screen screen says how (table H).
 */
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';

/** Where the page's events come from (the window); returns what stops listening. */
export type Listen = (type: 'beforeinstallprompt' | 'appinstalled', listener: (event: unknown) => void) => () => void;

/** An event's method, bound, or null when it has none. */
function method(event: unknown, name: string): (() => unknown) | null {
  if (typeof event !== 'object' || event === null) return null;
  const found: unknown = Reflect.get(event, name);
  return typeof found === 'function' ? (): unknown => Reflect.apply(found, event, []) : null;
}

/** The browser's install offer, kept until the writer uses it. */
export class InstallPrompt {
  /** Whether "Install" is offered (P1). */
  readonly available: Accessor<boolean>;
  readonly #setAvailable: (available: boolean) => void;
  /** The kept event's prompt, or null. */
  #prompt: (() => unknown) | null = null;

  constructor() {
    const [available, setAvailable] = createSignal(false);
    this.available = available;
    this.#setAvailable = setAvailable;
  }

  /** P1, P3: listens on the page; returns what stops it. */
  start(listen: Listen): () => void {
    const stopOffer = listen('beforeinstallprompt', (event) => {
      const prompt = method(event, 'prompt');
      const preventDefault = method(event, 'preventDefault');
      if (prompt === null || preventDefault === null) return;
      preventDefault();
      this.#prompt = prompt;
      this.#setAvailable(true);
    });
    const stopInstalled = listen('appinstalled', () => {
      this.#forget();
    });
    return () => {
      stopOffer();
      stopInstalled();
    };
  }

  /** P2, P5: the browser's install prompt, once; nothing offered after, whatever the writer answers or if it throws. */
  install(): void {
    const prompt = this.#prompt;
    this.#forget();
    // P4: with no offer kept, `then(null)` does nothing.
    Promise.resolve()
      .then(prompt)
      .catch(() => undefined);
  }

  #forget(): void {
    this.#prompt = null;
    this.#setAvailable(false);
  }
}
